/* ============================================================
   APP EMULATOR — Multi-system emulator via EmulatorJS
   Supports: GB, GBC, GBA, NES, SNES, N64, Sega Genesis, NDS
   ROMs load via file picker, drag-and-drop onto screen, or URL
   EmulatorJS CDN: https://cdn.emulatorjs.org/stable/data/  (NOT npm —
   there is no `emulatorjs` package on npm; that path 404s.)
   ============================================================ */

(function () {
  'use strict';

  /* ── Built-in game library ───────────────────────────────────
     Freely-distributable Game Boy / Game Boy Color homebrew from the
     gbdev Homebrew Hub archive (https://hh.gbdev.io). These are original
     works by their listed authors — NOT commercial ROM dumps.

     Hosting: raw.githubusercontent.com is used because it is the only
     free host tested that returns `access-control-allow-origin: *`.
     EmulatorJS fetches EJS_gameUrl from the page, so a host without CORS
     fails outright — GitHub *release* assets, for example, are unusable
     here (no CORS header, and the URL redirects to a signed link that
     expires within the hour).

     This does run on gbdev's bandwidth. To mirror the ROMs into Shopify
     Files instead (recommended, and the pattern already used for the SWF
     games), upload each .gb/.gbc and repoint LIB_BASE at the CDN folder —
     the `file` paths below are the only thing that needs to match.

     `license` is what the Homebrew Hub entry declares. 'Not declared'
     means the archive lists no license: freely distributed there, but
     with no explicit grant. See the note in the commit/PR before shipping
     these on a storefront.
  ──────────────────────────────────────────────────────────── */
  var LIB_BASE = 'https://raw.githubusercontent.com/gbdev/database/master/entries/';

  /* Destination for the "Find…" button. Repoint this at your own game
     library when it's ready — this constant is the only place it appears.
     Whatever it points at must be somewhere you're happy linking a
     storefront to; it opens in a new tab with rel=noopener. */
  var FIND_URL = 'https://hh.gbdev.io/games';

  /* Official EmulatorJS CDN. The original code pointed at
     cdn.jsdelivr.net/npm/emulatorjs@4.0.9/ — there is no `emulatorjs`
     package on npm, so loader.js 404'd and every boot failed with
     "could not load EmulatorJS. Check network." This host serves the
     real distribution and sends `access-control-allow-origin: *`.
     `stable` tracks the current release; pin to a version tag if a
     future release regresses. */
  var EJS_DATA = 'https://cdn.emulatorjs.org/stable/data/';

  /* Which on-screen gamepad to use.

     EmulatorJS ships its own virtual gamepad — more buttons, better feel —
     but it binds "touchstart touchend touchcancel" and nothing else, so with
     a mouse its buttons are completely inert. It also gates itself behind an
     internal hasTouchScreen check. Our own pad uses pointer events and works
     with either input.

     So: touch devices get EmulatorJS's (repositioned below the screen —
     by default it floats on top of the game), desktop keeps ours. Never both
     at once, which is what put two D-pads on the phone screenshot. */
  var HAS_TOUCH = (function () {
    try {
      /* ?emu_touch=1 / =0 forces either layout, so the touch build can be
         previewed on a desktop browser without a phone. Detection otherwise. */
      var forced = new URLSearchParams(window.location.search).get('emu_touch');
      if (forced === '1') return true;
      if (forced === '0') return false;
      return (window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches) ||
             navigator.maxTouchPoints > 0;
    } catch (e) { return false; }
  })();

  /* Height of the control strip below the game when the built-in gamepad is
     in use. Its zones anchor with `bottom` offsets from the bottom of the
     game container and stack ~225px tall, so the canvas must stop short of
     this or the D-pad overlaps it. Shared by the layout CSS and the
     in-frame canvas clamp so the two cannot drift. */
  var PAD_STRIP = 230;

  var LIBRARY = [
    { title: 'Deadeus',                    dev: 'izma',                sys: 'gb',  license: 'Not declared',       file: 'deadeus/deadeus.gb' },
    { title: 'Dangan GB',                  dev: 'Snorpung & Nordloef', sys: 'gb',  license: 'Not declared',       file: 'dangan/dangan.gb' },
    { title: 'Libbet and the Magic Floor', dev: 'Damian Yerrick',      sys: 'gb',  license: 'Not declared',       file: 'libbet/libbet.gb' },
    { title: 'Carazu',                     dev: 'Martin Holtkamp',     sys: 'gb',  license: 'GPL-3.0-or-later',   file: 'carazu/carazu.gb' },
    { title: 'Geometrix',                  dev: 'AntonioND',           sys: 'gbc', license: 'GPL-3.0-or-later',   file: 'geometrix/geometrix.gbc' },
    { title: 'Retroid',                    dev: 'Jonas Fischbach',     sys: 'gb',  license: 'Not declared',       file: 'retroid/Retroid.gb' },
    { title: 'Into The Blue',              dev: 'Jonas Fischbach',     sys: 'gb',  license: 'Not declared',       file: 'into-the-blue/into-the-blue.gb' },
    { title: 'Black Castle',               dev: 'User0x7f',            sys: 'gb',  license: 'Not declared',       file: 'black-castle/black-castle.gb' },
  ];

  /* ── CSS ─────────────────────────────────────────────────────
     All classes are prefixed with .emu- to avoid collisions.
     #emu-area and #emu-game are safe as unique IDs (singleton window).
  ──────────────────────────────────────────────────────────── */
  var CSS = [
    '.emu-wrap{display:flex;flex-direction:column;height:100%;font-family:"Geneva","Lucida Sans",sans-serif;font-size:12px;background:#d4d0c8;}',

    /* Scrollable body */
    /* Column flex + margin-top:auto on .emu-controls glues the drawer to the
       bottom of the window instead of letting it float directly under the
       gamepad with dead grey below it. */
    /* min-height:0 lets the bezel actually shrink inside this flex column —
       without it a flex item refuses to go below its content size and the
       console pushes the drawer off the bottom again. */
    '.emu-body{flex:1;min-height:0;overflow-y:auto;padding:10px 12px 8px;display:flex;flex-direction:column;}',
    '.emu-controls{margin-top:auto;}',

    /* Game Boy bezel */
    '.emu-bezel{background:#2a2a2a;border-radius:8px 8px 20px 20px;padding:8px 8px 14px;box-shadow:inset 0 2px 4px rgba(0,0,0,0.6),0 4px 10px rgba(0,0,0,0.5);margin-bottom:8px;}',
    '.emu-screen-surround{background:#1a1a1a;border-radius:4px;padding:6px;position:relative;}',
    '.emu-screen-label{font-size:9px;color:#888;text-align:center;letter-spacing:3px;text-transform:uppercase;margin-bottom:4px;font-family:Arial,sans-serif;}',

    /* Power LED */
    '.emu-power-led{width:6px;height:6px;border-radius:50%;background:#222;position:absolute;right:10px;top:8px;transition:all 0.3s;}',
    '.emu-power-led.on{background:#00ff44;box-shadow:0 0 6px #00ff44;}',

    /* Emulator screen — GB green with scanline overlay */
    '#emu-area{width:100%;aspect-ratio:10/9;background:#8bac0f;' +
      'background-image:repeating-linear-gradient(0deg,rgba(0,0,0,0.03) 0px,rgba(0,0,0,0.03) 1px,transparent 1px,transparent 2px),' +
      'repeating-linear-gradient(90deg,rgba(0,0,0,0.03) 0px,rgba(0,0,0,0.03) 1px,transparent 1px,transparent 2px);' +
      'border-radius:2px;overflow:hidden;position:relative;display:flex;align-items:center;justify-content:center;}',
    '#emu-area::after{content:"";position:absolute;inset:0;' +
      'background:repeating-linear-gradient(0deg,transparent,transparent 1px,rgba(0,0,0,0.08) 1px,rgba(0,0,0,0.08) 2px);' +
      'pointer-events:none;z-index:10;}',
    '#emu-game{width:100%;height:100%;border:none;display:block;}',

    /* Touch layout: the screen area grows by PAD_STRIP so the built-in
       gamepad has somewhere to sit below the picture. padding-top:90% keeps
       the screen itself at the 10:9 ratio while the extra height stays a
       fixed pixel strip — content-box is required for padding and height to
       add up, since Dawn sets border-box globally. Absolutely positioned
       children resolve against the padding box, so inset:0 still covers the
       whole area. */
    /* Only while a game is actually running: the console stretches to fill
       whatever height is left after the drawer, and the strip is carved out
       of that. Sizing it as a fixed padding-top:90% + PAD_STRIP instead made
       the area 580px tall at phone width no matter what, which overflowed
       the viewport and pushed Boot/Reset off the bottom — and reserved
       gamepad space even on the idle INSERT ROM screen, where there are no
       buttons to hold. Flex means it cannot overflow by construction. */
    '.emu-wrap.is-touch.is-running .emu-bezel,' +
      '.emu-wrap.is-touch.is-running .emu-screen-surround{' +
      'display:flex;flex-direction:column;flex:1;min-height:0;}',
    '.emu-wrap.is-touch.is-running #emu-area{flex:1;min-height:0;aspect-ratio:auto;height:auto;}',
    '.emu-wrap.is-touch #emu-game,.emu-wrap.is-touch .emu-idle{position:absolute;inset:0;}',
    /* Scanlines belong over the screen, not over the buttons. */
    '.emu-wrap.is-touch.is-running #emu-area::after{bottom:' + PAD_STRIP + 'px;}',
    /* Our pad and theirs must never both be present. */
    '.emu-wrap.is-touch #emu-pad{display:none!important;}',

    /* Idle dots */
    '.emu-idle{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;height:100%;width:100%;}',
    '.emu-idle-dot{width:8px;height:8px;border-radius:50%;background:#306230;animation:emuPulse 2s ease-in-out infinite;}',
    '.emu-idle-dot:nth-child(2){animation-delay:0.3s;}',
    '.emu-idle-dot:nth-child(3){animation-delay:0.6s;}',
    '@keyframes emuPulse{0%,100%{opacity:0.3;}50%{opacity:1;}}',
    '.emu-idle-text{font-size:10px;color:#306230;font-family:monospace;letter-spacing:1px;margin-top:4px;}',

    /* Controls panel */
    '.emu-controls{background:#d4d0c8;border:1px solid #808080;border-radius:3px;box-shadow:inset 0 1px 0 #fff;padding:6px 8px;}',
    '.emu-row{display:flex;align-items:center;gap:6px;margin-bottom:5px;}',
    '.emu-row:last-child{margin-bottom:0;}',
    '.emu-label{font-size:10px;color:#444;white-space:nowrap;min-width:58px;}',
    '.emu-input{flex:1;height:18px;background:#fff;border:1px solid;border-color:#808080 #fff #fff #808080;box-shadow:inset 1px 1px 0 #000;font-size:10px;font-family:monospace;padding:0 4px;color:#000;outline:none;}',
    '.emu-input:focus{border-color:#000080 #fff #fff #000080;}',
    '.emu-select{cursor:pointer;padding:0 2px;}',

    /* OS9-style buttons */
    '.emu-btn{height:18px;padding:0 8px;background:linear-gradient(180deg,#e8e4dc 0%,#c8c4bc 50%,#b8b4ac 100%);' +
      'border:1px solid;border-color:#fff #808080 #808080 #fff;font-size:10px;font-family:inherit;cursor:default;white-space:nowrap;color:#000;border-radius:1px;}',
    '.emu-btn:active{background:linear-gradient(180deg,#b8b4ac 0%,#c8c4bc 50%,#e8e4dc 100%);border-color:#808080 #fff #fff #808080;}',
    '.emu-btn--primary{background:linear-gradient(180deg,#4040c0 0%,#2020a0 50%,#101080 100%);border-color:#8080ff #000040 #000040 #8080ff;color:#fff;}',
    '.emu-btn--primary:not([disabled]):active{background:linear-gradient(180deg,#101080 0%,#2020a0 50%,#4040c0 100%);}',
    '.emu-btn--primary[disabled]{opacity:0.4;}',

    /* Separator */
    '.emu-sep{height:1px;background:#808080;box-shadow:0 1px 0 #fff;margin:4px 0;}',

    /* ── Collapsible ROM/System drawer ──────────────────────────
       list-style:none + ::-webkit-details-marker kills the native triangle
       on both engines so the ▸ below is the only marker. */
    '.emu-drawer{margin:0;}',
    '.emu-drawer-summary{list-style:none;cursor:default;display:flex;align-items:center;gap:6px;' +
      'height:20px;padding:0 6px;font-size:10px;color:#000;user-select:none;-webkit-user-select:none;' +
      'background:linear-gradient(180deg,#e8e4dc 0%,#c8c4bc 100%);' +
      'border:1px solid;border-color:#fff #808080 #808080 #fff;border-radius:2px;}',
    '.emu-drawer-summary::-webkit-details-marker{display:none;}',
    '.emu-drawer-summary::before{content:"\\25B8";display:inline-block;font-size:9px;' +
      'transition:transform 0.12s ease;}',
    '.emu-drawer[open] > .emu-drawer-summary::before{transform:rotate(90deg);}',
    '.emu-drawer[open] > .emu-drawer-summary{border-color:#808080 #fff #fff #808080;}',
    '.emu-drawer-note{margin-left:auto;color:#555;font-style:italic;overflow:hidden;' +
      'text-overflow:ellipsis;white-space:nowrap;max-width:60%;}',
    '.emu-drawer[open] .emu-drawer-note{display:none;}',
    '.emu-drawer-body{padding:6px 0 2px;}',
    '.emu-actions{margin-top:6px;justify-content:flex-end;}',

    /* ── Touch gamepad ──────────────────────────────────────────
       touch-action:none is load-bearing: without it the browser claims
       the gesture for scrolling/zooming and swallows pointercancel, so a
       held direction sticks down. user-select/callout stop long-press
       selecting the button label on iOS. */
    '.emu-pad{display:flex;flex-direction:column;align-items:center;gap:6px;padding:8px 10px 10px;' +
      'background:#d4d0c8;border-top:1px solid #808080;box-shadow:inset 0 1px 0 #fff;flex-shrink:0;}',
    '.emu-pad[hidden]{display:none;}',
    '.emu-pad-btn{touch-action:none;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;' +
      '-webkit-tap-highlight-color:transparent;cursor:default;color:#000;font-family:inherit;font-weight:bold;' +
      'background:linear-gradient(180deg,#e8e4dc 0%,#c8c4bc 50%,#b8b4ac 100%);' +
      'border:2px solid;border-color:#fff #707070 #707070 #fff;}',
    '.emu-pad-btn.is-down{background:linear-gradient(180deg,#a8a4a0 0%,#c0bcb4 100%);' +
      'border-color:#707070 #fff #fff #707070;}',

    '.emu-pad-shoulders{display:flex;justify-content:space-between;width:100%;max-width:280px;}',
    '.emu-pad-btn--sh{width:64px;height:22px;font-size:11px;border-radius:3px 3px 6px 6px;}',

    '.emu-pad-main{display:flex;align-items:center;justify-content:space-between;width:100%;max-width:280px;}',

    /* D-pad: 3×3 grid, arms in the cross cells, hub covers the seams */
    '.emu-dpad{position:relative;display:grid;grid-template-columns:repeat(3,30px);grid-template-rows:repeat(3,30px);}',
    '.emu-dpad-btn{width:30px;height:30px;padding:0;}',
    '.emu-dpad-btn--up{grid-area:1/2;border-radius:5px 5px 0 0;}',
    '.emu-dpad-btn--left{grid-area:2/1;border-radius:5px 0 0 5px;}',
    '.emu-dpad-btn--right{grid-area:2/3;border-radius:0 5px 5px 0;}',
    '.emu-dpad-btn--down{grid-area:3/2;border-radius:0 0 5px 5px;}',
    '.emu-dpad-hub{grid-area:2/2;background:#c8c4bc;border-top:2px solid #c8c4bc;border-bottom:2px solid #c8c4bc;pointer-events:none;}',

    '.emu-pad-face{display:flex;align-items:center;gap:10px;transform:rotate(-20deg);}',
    '.emu-pad-btn--ab{width:38px;height:38px;border-radius:50%;font-size:14px;color:#8b1a4a;}',

    /* Gentler tilt than the face buttons: at -20deg the rotated bounding box
       grew tall enough to push these under the status bar. */
    '.emu-pad-start{display:flex;gap:16px;margin-top:2px;}',
    '.emu-pad-btn--ss{height:18px;padding:0 12px;font-size:9px;letter-spacing:0.5px;border-radius:9px;transform:rotate(-12deg);}',

    /* Status bar */
    '.emu-statusbar{background:#d4d0c8;border-top:1px solid #808080;padding:2px 8px;font-size:9px;color:#444;font-family:monospace;display:flex;align-items:center;gap:8px;min-height:14px;flex-shrink:0;}',
    '.emu-status-div{width:1px;height:10px;background:#808080;}',

    /* Boot scan line animation */
    '@keyframes emuBootScan{0%{transform:translateY(-100%);opacity:0;}10%{opacity:1;}100%{transform:translateY(100%);opacity:0;}}',
    '.emu-boot-scan{position:absolute;inset:0;height:40%;' +
      'background:linear-gradient(180deg,transparent 0%,rgba(143,185,20,0.6) 50%,transparent 100%);' +
      'animation:emuBootScan 0.8s ease-in-out;z-index:5;pointer-events:none;}'
  ].join('');

  function injectCSS() {
    if (document.getElementById('os9-emulator-css')) return;
    var s = document.createElement('style');
    s.id = 'os9-emulator-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ── HTML builder ─────────────────────────────────────────── */
  function buildHTML() {
    return '<div class="emu-wrap' + (HAS_TOUCH ? ' is-touch' : '') + '">' +
      '<div class="emu-body">' +
        '<div class="emu-bezel">' +
          '<div class="emu-screen-surround">' +
            '<div class="emu-screen-label">Game Boy</div>' +
            '<div class="emu-power-led" id="emu-power-led"></div>' +
            '<div id="emu-area">' +
              '<div class="emu-idle" id="emu-idle">' +
                '<div class="emu-idle-dot"></div>' +
                '<div class="emu-idle-dot"></div>' +
                '<div class="emu-idle-dot"></div>' +
                '<div class="emu-idle-text">INSERT ROM</div>' +
              '</div>' +
              /* EmulatorJS mounts here — hidden until boot */
              '<div id="emu-game" style="display:none;width:100%;height:100%;"></div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        /* Touch gamepad. Hidden until a game boots — before that it is just
           dead space in an already-tight window. data-btn values are libretro
           RETRO_DEVICE_ID_JOYPAD indices, passed straight to simulateInput. */
        '<div class="emu-pad" id="emu-pad" hidden>' +
          '<div class="emu-pad-shoulders">' +
            '<button class="emu-pad-btn emu-pad-btn--sh" data-btn="10">L</button>' +
            '<button class="emu-pad-btn emu-pad-btn--sh" data-btn="11">R</button>' +
          '</div>' +
          '<div class="emu-pad-main">' +
            '<div class="emu-dpad">' +
              '<button class="emu-pad-btn emu-dpad-btn emu-dpad-btn--up"    data-btn="4"  aria-label="Up"></button>' +
              '<button class="emu-pad-btn emu-dpad-btn emu-dpad-btn--left"  data-btn="6"  aria-label="Left"></button>' +
              '<span class="emu-dpad-hub"></span>' +
              '<button class="emu-pad-btn emu-dpad-btn emu-dpad-btn--right" data-btn="7"  aria-label="Right"></button>' +
              '<button class="emu-pad-btn emu-dpad-btn emu-dpad-btn--down"  data-btn="5"  aria-label="Down"></button>' +
            '</div>' +
            '<div class="emu-pad-face">' +
              '<button class="emu-pad-btn emu-pad-btn--ab" data-btn="0">B</button>' +
              '<button class="emu-pad-btn emu-pad-btn--ab" data-btn="8">A</button>' +
            '</div>' +
          '</div>' +
          '<div class="emu-pad-start">' +
            '<button class="emu-pad-btn emu-pad-btn--ss" data-btn="2">SELECT</button>' +
            '<button class="emu-pad-btn emu-pad-btn--ss" data-btn="3">START</button>' +
          '</div>' +
        '</div>' +
        '<div class="emu-controls">' +
          /* Native <details> disclosure — no JS to open/close, keyboard and
             screen-reader behaviour for free, and it collapses the four
             config rows that were crowding the gamepad off a phone screen.
             Open by default so a first-time visitor sees the Library. */
          '<details class="emu-drawer" id="emu-drawer" open>' +
          '<summary class="emu-drawer-summary">ROM &amp; System' +
            '<span class="emu-drawer-note" id="emu-drawer-note"></span></summary>' +
          '<div class="emu-drawer-body">' +
          '<div class="emu-row">' +
            '<span class="emu-label">Library:</span>' +
            '<select class="emu-input emu-select" id="emu-library">' +
              '<option value="">— Pick a homebrew game —</option>' +
              LIBRARY.map(function (g, i) {
                return '<option value="' + i + '">' + g.title + ' — ' + g.dev + '</option>';
              }).join('') +
            '</select>' +
          '</div>' +
          '<div class="emu-sep"></div>' +
          '<div class="emu-row">' +
            '<span class="emu-label">Load ROM:</span>' +
            '<input class="emu-input" id="emu-rom-name" type="text" placeholder="No ROM loaded" readonly>' +
            '<button class="emu-btn" id="emu-browse-btn">Browse…</button>' +
            /* Hidden file input — triggered by Browse button */
            '<input type="file" id="emu-file-input" accept=".gb,.gbc,.gba,.nes,.snes,.sfc,.smc,.n64,.z64,.md,.smd,.gen,.nds" style="display:none;">' +
          '</div>' +
          '<div class="emu-row">' +
            '<span class="emu-label">ROM URL:</span>' +
            '<input class="emu-input" id="emu-rom-url" type="text" placeholder="https://… or local server IP">' +
            '<button class="emu-btn" id="emu-url-btn">Load</button>' +
            '<button class="emu-btn" id="emu-find-btn" title="Browse the game library">Find…</button>' +
          '</div>' +
          '<div class="emu-sep"></div>' +
          '<div class="emu-row">' +
            '<span class="emu-label">System:</span>' +
            '<select class="emu-input emu-select" id="emu-system" style="flex:0.7;">' +
              '<option value="gb">Game Boy</option>' +
              '<option value="gbc">Game Boy Color</option>' +
              '<option value="gba">Game Boy Advance</option>' +
              '<option value="nes">NES</option>' +
              '<option value="snes">SNES</option>' +
              '<option value="n64">Nintendo 64</option>' +
              '<option value="genesis">Sega Genesis</option>' +
              '<option value="nds">Nintendo DS</option>' +
            '</select>' +
          '</div>' +
          '</div>' +          /* /emu-drawer-body */
          '</details>' +
          /* Boot and Reset live outside the drawer: you need them straight
             after picking a game, and hunting for them inside a collapsed
             panel on a phone was the whole problem. */
          '<div class="emu-row emu-actions">' +
            '<button class="emu-btn emu-btn--primary" id="emu-boot-btn" disabled>▶ Boot</button>' +
            '<button class="emu-btn" id="emu-reset-btn">Reset</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="emu-statusbar">' +
        '<span id="emu-status">Ready.</span>' +
        '<div class="emu-status-div"></div>' +
        '<span id="emu-sys-status">No system loaded</span>' +
        '<div class="emu-status-div"></div>' +
        '<span>EmulatorJS</span>' +
      '</div>' +
    '</div>';
  }

  /* ── Event wiring ─────────────────────────────────────────── */
  function wireEvents(body, infobar) {
    /* All ROM state is scoped here — no globals needed except EJS_* at boot time */
    var romBlob = null;
    var romURL  = null;
    var currentObjectURL = null;

    var fileInput   = body.querySelector('#emu-file-input');
    var romNameEl   = body.querySelector('#emu-rom-name');
    var romUrlEl    = body.querySelector('#emu-rom-url');
    var bootBtn     = body.querySelector('#emu-boot-btn');
    var resetBtn    = body.querySelector('#emu-reset-btn');
    var browseBtn   = body.querySelector('#emu-browse-btn');
    var urlBtn      = body.querySelector('#emu-url-btn');
    var systemSel   = body.querySelector('#emu-system');
    var librarySel  = body.querySelector('#emu-library');
    var findBtn     = body.querySelector('#emu-find-btn');
    var padEl       = body.querySelector('#emu-pad');
    var wrapEl      = body.querySelector('.emu-wrap');
    var drawerEl    = body.querySelector('#emu-drawer');
    var drawerNote  = body.querySelector('#emu-drawer-note');
    var powerLed    = body.querySelector('#emu-power-led');
    var idleEl      = body.querySelector('#emu-idle');
    var gameEl      = body.querySelector('#emu-game');
    var areaEl      = body.querySelector('#emu-area');
    var statusEl    = body.querySelector('#emu-status');
    var sysStatusEl = body.querySelector('#emu-sys-status');

    /* Extension → EmulatorJS core name map */
    var EXT_MAP = {
      gb: 'gb', gbc: 'gbc', gba: 'gba',
      nes: 'nes', snes: 'snes', sfc: 'snes', smc: 'snes',
      n64: 'n64', z64: 'n64',
      md: 'genesis', smd: 'genesis', gen: 'genesis',
      nds: 'nds'
    };

    function selectedSystemName() {
      var opt = systemSel.options[systemSel.selectedIndex];
      return opt ? opt.text : '';
    }

    function updateSysStatus() {
      sysStatusEl.textContent = 'System: ' + selectedSystemName();
    }

    function autoDetect(filename) {
      var ext = filename.split('.').pop().toLowerCase();
      if (EXT_MAP[ext]) {
        systemSel.value = EXT_MAP[ext];
        updateSysStatus();
      }
    }

    function loadROMFile(file) {
      romBlob = file; romURL = null;
      romUrlEl.value = '';
      romNameEl.value = file.name;
      bootBtn.disabled = false;
      statusEl.textContent = 'ROM loaded: ' + file.name + ' (' + (file.size / 1024).toFixed(0) + ' KB)';
      autoDetect(file.name);
    }

    browseBtn.addEventListener('click', function () { fileInput.click(); });

    fileInput.addEventListener('change', function (e) {
      var f = e.target.files[0];
      if (f) loadROMFile(f);
    });

    urlBtn.addEventListener('click', function () {
      var url = romUrlEl.value.trim();
      if (!url) return;
      romURL = url; romBlob = null;
      var name = url.split('/').pop() || 'remote-rom';
      romNameEl.value = name;
      bootBtn.disabled = false;
      statusEl.textContent = 'Remote ROM: ' + url;
      autoDetect(name);
    });

    /* Library pick behaves exactly like a URL load, but the system comes
       from the entry rather than extension sniffing — some .gb files in the
       archive are GBC-enhanced, so the declared core is more reliable. */
    librarySel.addEventListener('change', function () {
      var g = LIBRARY[librarySel.value];
      if (!g) return;
      romURL = LIB_BASE + g.file.split('/').map(encodeURIComponent).join('/');
      romBlob = null;
      romUrlEl.value = romURL;
      romNameEl.value = g.title;
      systemSel.value = g.sys;
      updateSysStatus();
      bootBtn.disabled = false;
      statusEl.textContent = g.title + ' — ' + g.dev + ' (' + g.license + ')';
      if (infobar) infobar.textContent = g.title + ' — ' + g.dev;
    });

    romUrlEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') urlBtn.click();
    });

    systemSel.addEventListener('change', updateSysStatus);

    findBtn.addEventListener('click', function () {
      window.open(FIND_URL, '_blank', 'noopener,noreferrer');
    });

    /* ── Touch gamepad ────────────────────────────────────────────
       Buttons drive gameManager.simulateInput(player, id, value) rather
       than synthetic key events: EmulatorJS's keyboard mapping is
       user-remappable, so faking keys would break for anyone who changed
       their bindings. The libretro ids in data-btn are stable.

       Pointer events (not touch/mouse) so one code path covers finger,
       stylus and mouse, and so multi-touch works — pressing A while
       holding Right is table stakes for a platformer. */
    var heldButtons = new Set();

    /* A mouse click's press and release land within a millisecond or two of
       each other. The core only samples input once per emulated frame (~16ms
       at 60fps), so a real click was being missed entirely — the button
       looked dead. Hold every press for at least this long before letting
       the release through. Taps on a touchscreen are usually longer than
       this and pass through unchanged. */
    var MIN_PRESS_MS = 90;
    var pressedAt = new Map();      /* id → timestamp of press   */
    var pendingRelease = new Map(); /* id → deferred release timer */

    function padInput(id, down) {
      var w = frameWindow();
      if (!w || !w.EJS_emulator || !w.EJS_emulator.gameManager) return;
      try { w.EJS_emulator.gameManager.simulateInput(0, id, down ? 1 : 0); } catch (err) {}
    }

    function releaseAllPadButtons() {
      pendingRelease.forEach(function (t) { clearTimeout(t); });
      pendingRelease.clear();
      pressedAt.clear();
      heldButtons.forEach(function (id) { padInput(id, false); });
      heldButtons.clear();
      padEl.querySelectorAll('.is-down').forEach(function (b) { b.classList.remove('is-down'); });
    }

    padEl.addEventListener('pointerdown', function (ev) {
      var btn = ev.target.closest('[data-btn]');
      if (!btn) return;
      ev.preventDefault();
      /* Capture so the matching pointerup lands here even if the finger
         slides off the button — otherwise the input sticks down. */
      try { btn.setPointerCapture(ev.pointerId); } catch (err) {}
      var id = Number(btn.dataset.btn);
      /* Re-pressing before the previous deferred release fired would other-
         wise let that timer cancel the new press. */
      if (pendingRelease.has(id)) {
        clearTimeout(pendingRelease.get(id));
        pendingRelease.delete(id);
      }
      heldButtons.add(id);
      pressedAt.set(id, Date.now());
      btn.classList.add('is-down');
      padInput(id, true);
    });

    function padRelease(ev) {
      var btn = ev.target.closest('[data-btn]');
      if (!btn) return;
      var id = Number(btn.dataset.btn);
      btn.classList.remove('is-down');

      function finish() {
        pendingRelease.delete(id);
        pressedAt.delete(id);
        heldButtons.delete(id);
        padInput(id, false);
      }
      var heldFor = Date.now() - (pressedAt.get(id) || 0);
      if (heldFor < MIN_PRESS_MS) {
        pendingRelease.set(id, setTimeout(finish, MIN_PRESS_MS - heldFor));
      } else {
        finish();
      }
    }
    padEl.addEventListener('pointerup', padRelease);
    padEl.addEventListener('pointercancel', padRelease);

    /* Backstop: if the window loses focus mid-press the pointerup never
       arrives, and the emulator would hold that direction forever. */
    window.addEventListener('blur', releaseAllPadButtons);
    padEl.addEventListener('contextmenu', function (ev) { ev.preventDefault(); });

    /* Drag & drop ROM directly onto the screen — auto-boots on drop */
    areaEl.addEventListener('dragover', function (e) {
      e.preventDefault();
      areaEl.style.outline = '2px dashed #8bac0f';
    });
    areaEl.addEventListener('dragleave', function () {
      areaEl.style.outline = '';
    });
    areaEl.addEventListener('drop', function (e) {
      e.preventDefault();
      areaEl.style.outline = '';
      var f = e.dataTransfer.files[0];
      if (!f) return;
      loadROMFile(f);
      setTimeout(function () { bootBtn.click(); }, 100);
    });

    /* Each boot runs in a throwaway, same-origin iframe.

       EmulatorJS's emulator.min.js declares top-level bindings (EJS_STORAGE,
       EJS_GameManager, EJS_Runtime …), so evaluating it a second time in one
       document throws "Identifier 'EJS_STORAGE' has already been declared".
       The loader re-appends that script unconditionally, so every boot after
       the first died on a SyntaxError and silently never started — leaving
       the PREVIOUS game running underneath, which is why its music carried
       over. Clearing the container could never have fixed that; the surviving
       emulator was the only one still alive.

       A fresh document per boot sidesteps the redeclaration entirely, and
       dropping the iframe tears down the emulation loop, the WebAudio context
       and every EJS_* global in one move — no reliance on EmulatorJS internals
       that differ between releases. srcdoc keeps the frame on our own origin
       so the touch gamepad can reach in and call simulateInput. */
    function frameWindow() {
      var f = gameEl.querySelector('#emu-frame');
      try { return (f && f.contentWindow) || null; } catch (err) { return null; }
    }

    function emulatorFrameHTML(gameUrl, core) {
      return '<!doctype html><html><head><meta charset="utf-8">' +
        '<style>html,body{margin:0;height:100%;background:#000;overflow:hidden}' +
        '#g{width:100%;height:100%}' +
        (HAS_TOUCH
          /* Touch: keep EmulatorJS's gamepad but lift it out of the way of
             the game. Its buttons are anchored to the bottom of this
             container, so pinning the canvas to the top and reserving
             PAD_STRIP below drops the whole control cluster into that strip
             instead of floating over the picture. !important is required —
             EmulatorJS sets canvas width/height inline on every resize, and
             an important rule outranks a plain inline style. */
          /* Box the canvas over the top region and let object-fit letterbox
             the picture inside it. Sizing it with width/height:auto instead
             would collapse the canvas to its intrinsic 160×144 — EmulatorJS
             normally scales it via inline width/height, which these
             !important rules replace. */
          ? '#g canvas{position:absolute!important;top:0!important;left:0!important;' +
              'width:100%!important;height:calc(100% - ' + PAD_STRIP + 'px)!important;' +
              'object-fit:contain!important;transform:none!important;}' +
            /* EmulatorJS gates its gamepad behind its own touch check, which
               is false under the ?emu_touch=1 desktop preview. Re-assert it
               so the forced layout is actually visible. On a real touch
               device this is a no-op. */
            /* The strip keeps EmulatorJS's own black container background,
               which reads correctly inside the Game Boy bezel — screen and
               buttons look like one moulded shell. Painting it OS9 grey here
               would have no effect anyway: .ejs_parent covers the body. */
            '.ejs_virtualGamepad_parent{display:block!important}'
          /* No touch: their buttons would be dead pixels, so hide them and
             let our pointer-driven pad handle it. */
          : '.ejs_virtualGamepad_parent{display:none!important}'
        ) +
        '</style></head><body><div id="g"></div>' +
        '<script>' +
          'window.EJS_player=' + JSON.stringify('#g') + ';' +
          'window.EJS_core=' + JSON.stringify(core) + ';' +
          'window.EJS_gameUrl=' + JSON.stringify(gameUrl) + ';' +
          'window.EJS_pathtodata=' + JSON.stringify(EJS_DATA) + ';' +
          'window.EJS_startOnLoaded=true;' +
          'window.EJS_color="#306230";' +
          'window.EJS_backgroundColor="#000";' +
          'window.EJS_volume=0.5;' +
        '</script>' +
        '<script src="' + EJS_DATA + 'loader.js"></script>' +
        '</body></html>';
    }

    function startEmulator(gameUrl) {
      var frame = document.createElement('iframe');
      frame.id = 'emu-frame';
      frame.title = 'Emulator';
      frame.style.cssText = 'width:100%;height:100%;border:0;display:block;background:#000;';
      frame.srcdoc = emulatorFrameHTML(gameUrl, systemSel.value);
      gameEl.appendChild(frame);

      /* No usable load/error event reaches us from inside srcdoc, so poll for
         the emulator object and time out rather than leaving "Booting…" up
         forever if the ROM URL is wrong or the CDN is unreachable. */
      var deadline = Date.now() + 25000;
      (function poll() {
        if (!frame.isConnected) return;              /* torn down meanwhile */
        var w = frameWindow();
        if (w && w.EJS_emulator) {
          statusEl.textContent = 'Running: ' + romNameEl.value;
          if (infobar) infobar.textContent = 'Running: ' + romNameEl.value;
          return;
        }
        if (Date.now() > deadline) {
          statusEl.textContent = 'Error: emulator did not start. Check the ROM URL, the system setting, and your connection.';
          return;
        }
        setTimeout(poll, 300);
      })();
    }

    /* Dropping the iframe is the whole teardown: the emulation loop, the
       WebAudio context and every EJS_* global live inside that document and
       die with it. Release held pad buttons first, while the frame is still
       reachable, so nothing is left latched if it is ever re-created. */
    function teardownEmulator() {
      releaseAllPadButtons();
      gameEl.innerHTML = '';
      padEl.hidden = true;
      wrapEl.classList.remove('is-running');   /* back to the plain 10:9 screen */
    }

    bootBtn.addEventListener('click', function () {
      if (!romBlob && !romURL) return;

      /* Drop any running game before starting the next one — this is the
         fix for the overlapping-music bug. Must happen before the display
         changes below, since teardown re-hides the gamepad. */
      teardownEmulator();

      /* Collapse the config drawer once a game is running so the screen and
         the gamepad get the vertical space — the whole point of the drawer. */
      drawerEl.open = false;
      drawerNote.textContent = romNameEl.value || '';

      statusEl.textContent = 'Booting ' + selectedSystemName() + '…';
      if (infobar) infobar.textContent = 'Booting ' + selectedSystemName() + '…';

      /* Green scan line boot animation */
      var scan = document.createElement('div');
      scan.className = 'emu-boot-scan';
      areaEl.appendChild(scan);
      setTimeout(function () { if (scan.parentNode) scan.parentNode.removeChild(scan); }, 800);

      idleEl.style.display = 'none';
      gameEl.style.display = 'block';
      powerLed.classList.add('on');
      padEl.hidden = false;
      wrapEl.classList.add('is-running');   /* opens up the gamepad strip on touch */

      if (romBlob) {
        if (currentObjectURL) URL.revokeObjectURL(currentObjectURL);
        currentObjectURL = URL.createObjectURL(romBlob);
        startEmulator(currentObjectURL);
      } else {
        startEmulator(romURL);
      }
    });

    resetBtn.addEventListener('click', function () {
      teardownEmulator();
      drawerEl.open = true;          /* nothing loaded — show the controls again */
      drawerNote.textContent = '';
      gameEl.style.display = 'none';
      idleEl.style.display = 'flex';
      powerLed.classList.remove('on');

      if (currentObjectURL) { URL.revokeObjectURL(currentObjectURL); currentObjectURL = null; }
      romBlob = null; romURL = null;
      romNameEl.value = ''; romUrlEl.value = ''; fileInput.value = '';
      bootBtn.disabled = true;
      statusEl.textContent = 'Ready.';
      sysStatusEl.textContent = 'No system loaded';
      if (infobar) infobar.textContent = 'EmulatorJS — Ready';
    });

    updateSysStatus();
  }

  /* ── Open window ──────────────────────────────────────────── */
  function openEmulator() {
    if (!window.OS9Windows) return;
    injectCSS();

    var existing = document.getElementById('os9-emulator-win');
    if (existing) {
      document.querySelectorAll('.os9-win').forEach(function (w) { w.classList.remove('is-active'); });
      existing.classList.add('is-active');
      existing.style.zIndex = String(++window._os9TopZ || 1200);
      return;
    }

    var desktop = document.getElementById('os9-desktop-area') || document.body;
    var dw = desktop.offsetWidth  || window.innerWidth  - 96;
    var dh = desktop.offsetHeight || window.innerHeight - 48;

    /* 700 rather than the original 620: the touch gamepad adds ~150px and
       START/SELECT were being clipped behind the status bar. Clamped to the
       desktop so it still fits on short viewports, where the body scrolls. */
    var winW = 480;
    var winH = Math.min(700, Math.max(480, dh - 20));

    var el = window.OS9Windows._buildWindow({
      id:    'os9-emulator-win',
      title: 'Game Boy Player',
      width:  winW,
      height: winH,
      x: Math.max(0, Math.round((dw - winW) / 2)),
      y: Math.max(0, Math.round((dh - winH) / 2))
    });

    var body    = el.querySelector('[data-body]');
    var infobar = el.querySelector('[data-infobar]');
    body.style.cssText = 'padding:0;overflow:hidden;display:flex;flex:1;flex-direction:column;';
    body.innerHTML = buildHTML();
    if (infobar) infobar.style.display = 'none';
    wireEvents(body, infobar);

    desktop.appendChild(el);
    window.OS9Windows._initWindow(el, 'os9-emulator-win');

    /* Mobile fullscreen — fills the desktop area so the title bar stays visible.
       Uses position:absolute (same as all OS9 windows) rather than fixed,
       which would slide the window under the menu bar. */
    var isMobile = window.innerWidth <= 749;
    var sidebar  = document.querySelector('.os9-sidebar');
    if (isMobile) {
      if (sidebar) sidebar.style.display = 'none';
      el.style.left   = '0';
      el.style.top    = '0';
      el.style.width  = '100%';
      el.style.height = '100%';
    }

    /* Restore sidebar on close or minimize; re-hide on un-minimize */
    if (isMobile && sidebar) {
      var closeBtn    = el.querySelector('[data-close]');
      var minimizeBtn = el.querySelector('[data-minimize]');
      if (closeBtn)    closeBtn.addEventListener('click', function () { sidebar.style.display = ''; });
      if (minimizeBtn) {
        var isMinimized = false;
        minimizeBtn.addEventListener('click', function () {
          isMinimized = !isMinimized;
          sidebar.style.display = isMinimized ? '' : 'none';
        });
      }
    }
  }

  /*
   * Signal flow:
   *   os9-finder.js openItem() — action:'emulator' → window.AppEmulator.open()
   *   openEmulator() → _buildWindow + _initWindow (OS9 window manager)
   *   wireEvents() owns all ROM state locally — no globals except EJS_* at boot time
   *   startEmulator() sets EJS_* globals → injects loader.js from jsDelivr CDN
   *   EmulatorJS mounts into #emu-game and takes over from there
   */
  window.AppEmulator = { open: openEmulator };
})();